import type { PrismaClient } from '@prisma/client';
import express, { Router, type Request } from 'express';
import { z } from 'zod';
import type { Config } from '../../core/config.js';
import { HttpError } from '../../core/errors.js';
import { asyncHandler, clientIp, pageParams, parse } from '../../core/http.js';
import { authenticate, permissionsGranted } from '../auth/auth.middleware.js';
import { MAX_CSV_BYTES } from './csv.parser.js';
import { getImportEntity, listImportEntities } from './csv.registry.js';
import {
  batchView,
  commitBatch,
  createBatch,
  dryRun,
  errorReportCsv,
  rollbackBatch,
  templateCsv,
} from './csv.service.js';

interface Deps {
  db: PrismaClient;
  config: Config;
}

export function createCsvRouter({ db, config }: Deps) {
  const router = Router();
  router.use('/csv', authenticate({ db, config }));

  const actor = (req: Request) => ({
    companyId: req.auth!.companyId,
    userId: req.auth!.userId,
    ip: clientIp(req),
  });

  function entityFor(req: Request, key: string) {
    const entity = getImportEntity(key);
    if (!entity) throw new HttpError(404, 'NOT_FOUND', 'Unknown import type');
    if (!permissionsGranted(req.auth!.permissions, entity.importPermission)) {
      throw new HttpError(403, 'FORBIDDEN', 'You do not have permission to import this');
    }
    return entity;
  }

  router.get('/csv/entities', (req, res) => {
    res.json({
      items: listImportEntities()
        .filter((e) => permissionsGranted(req.auth!.permissions, e.importPermission))
        .map((e) => ({ key: e.key, label: e.label, columns: e.columns })),
    });
  });

  router.get(
    '/csv/batches',
    asyncHandler(async (req, res) => {
      const { page, pageSize, skip, take } = pageParams(req.query as Record<string, unknown>, 20);
      const entities = listImportEntities()
        .filter((e) => permissionsGranted(req.auth!.permissions, e.importPermission))
        .map((e) => e.key);
      const where = {
        companyId: req.auth!.companyId,
        entity: req.query.entity ? String(req.query.entity) : { in: entities },
      };
      const [total, rows] = await Promise.all([
        db.importBatch.count({ where }),
        db.importBatch.findMany({
          where,
          orderBy: { id: 'desc' },
          skip,
          take,
          omit: { rawCsv: true },
        }),
      ]);
      res.json({ items: rows.map(batchView), total, page, pageSize });
    }),
  );

  router.get(
    '/csv/batches/:id',
    asyncHandler(async (req, res) => {
      const batch = await db.importBatch.findFirst({
        where: { id: Number(req.params.id), companyId: req.auth!.companyId },
        omit: { rawCsv: true },
      });
      if (!batch) throw new HttpError(404, 'NOT_FOUND', 'Import not found');
      entityFor(req, batch.entity);
      res.json(batchView(batch));
    }),
  );

  router.get(
    '/csv/batches/:id/errors.csv',
    asyncHandler(async (req, res) => {
      const batch = await db.importBatch.findFirst({
        where: { id: Number(req.params.id), companyId: req.auth!.companyId },
        select: { entity: true },
      });
      if (!batch) throw new HttpError(404, 'NOT_FOUND', 'Import not found');
      entityFor(req, batch.entity);
      res.setHeader('Content-Type', 'text/csv; charset=utf-8');
      res.setHeader(
        'Content-Disposition',
        `attachment; filename="import-${req.params.id}-errors.csv"`,
      );
      res.send('﻿' + (await errorReportCsv(db, req.auth!.companyId, Number(req.params.id))));
    }),
  );

  const runSchema = z.object({
    mapping: z.record(z.string()),
    duplicate: z.enum(['skip', 'update', 'fail']).default('skip'),
    asApproved: z.boolean().default(false),
    missingRef: z
      .object({
        mode: z.enum(['error', 'create', 'map']),
        map: z.record(z.number().int().positive()).optional(),
      })
      .default({ mode: 'error' }),
  });

  router.post(
    '/csv/batches/:id/dry-run',
    asyncHandler(async (req, res) => {
      const id = Number(req.params.id);
      const batch = await db.importBatch.findFirst({
        where: { id, companyId: req.auth!.companyId },
        select: { entity: true },
      });
      if (!batch) throw new HttpError(404, 'NOT_FOUND', 'Import not found');
      entityFor(req, batch.entity);
      const body = parse(runSchema, req.body);
      res.json(
        await dryRun(db, {
          companyId: req.auth!.companyId,
          userId: req.auth!.userId,
          batchId: id,
          mapping: body.mapping,
          options: {
            duplicate: body.duplicate,
            missingRef: body.missingRef,
            asApproved: body.asApproved,
          },
        }),
      );
    }),
  );

  router.post(
    '/csv/batches/:id/commit',
    asyncHandler(async (req, res) => {
      const id = Number(req.params.id);
      const batch = await db.importBatch.findFirst({
        where: { id, companyId: req.auth!.companyId },
        select: { entity: true },
      });
      if (!batch) throw new HttpError(404, 'NOT_FOUND', 'Import not found');
      entityFor(req, batch.entity);
      res.json(await commitBatch(db, actor(req) as never, id));
    }),
  );

  router.post(
    '/csv/batches/:id/rollback',
    asyncHandler(async (req, res) => {
      const id = Number(req.params.id);
      const batch = await db.importBatch.findFirst({
        where: { id, companyId: req.auth!.companyId },
        select: { entity: true },
      });
      if (!batch) throw new HttpError(404, 'NOT_FOUND', 'Import not found');
      entityFor(req, batch.entity);
      await rollbackBatch(db, actor(req) as never, id);
      res.status(204).end();
    }),
  );

  router.get('/csv/:entity/template', (req, res) => {
    const entity = entityFor(req, String(req.params.entity));
    res.setHeader('Content-Type', 'text/csv; charset=utf-8');
    res.setHeader('Content-Disposition', `attachment; filename="${entity.key}-template.csv"`);
    res.send('﻿' + templateCsv(entity));
  });

  // Upload: the request body IS the CSV (text/csv). Size and content type are enforced before parsing.
  router.post(
    '/csv/:entity/upload',
    express.text({
      type: ['text/csv', 'text/plain', 'application/csv', 'application/vnd.ms-excel'],
      limit: MAX_CSV_BYTES + 1024,
    }),
    asyncHandler(async (req, res) => {
      const entity = entityFor(req, String(req.params.entity));
      if (typeof req.body !== 'string')
        throw new HttpError(415, 'UNSUPPORTED_TYPE', 'Upload a .csv file (Content-Type: text/csv)');
      const filename = String(req.query.filename ?? 'import.csv');
      if (!/\.csv$/i.test(filename))
        throw new HttpError(415, 'UNSUPPORTED_TYPE', 'Only .csv files are accepted');
      res.status(201).json(
        await createBatch(db, {
          companyId: req.auth!.companyId,
          userId: req.auth!.userId,
          entity: entity.key,
          filename,
          text: req.body,
        }),
      );
    }),
  );

  router.get(
    '/csv/:entity/mappings',
    asyncHandler(async (req, res) => {
      entityFor(req, String(req.params.entity));
      res.json({
        items: await db.savedColumnMapping.findMany({
          where: { companyId: req.auth!.companyId, entity: String(req.params.entity) },
        }),
      });
    }),
  );

  router.post(
    '/csv/:entity/mappings',
    asyncHandler(async (req, res) => {
      const entity = entityFor(req, String(req.params.entity));
      const body = parse(
        z.object({ name: z.string().trim().min(2).max(100), mapping: z.record(z.string()) }),
        req.body,
      );
      const saved = await db.savedColumnMapping.upsert({
        where: {
          companyId_entity_name: {
            companyId: req.auth!.companyId,
            entity: entity.key,
            name: body.name,
          },
        },
        create: {
          companyId: req.auth!.companyId,
          entity: entity.key,
          name: body.name,
          mapping: body.mapping,
        },
        update: { mapping: body.mapping },
      });
      res.status(201).json(saved);
    }),
  );

  return router;
}
